import { useState, useRef, useEffect, useContext, useMemo } from "react";
import {
  Modal,
  FormControl,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  TextareaAutosize,
  Fab,
  Badge,
  Tooltip,
} from "@material-ui/core";
import { ResizableBox } from "react-resizable";
import DragHandleIcon from "@material-ui/icons/DragHandle";
import { KeyboardDatePicker } from "@material-ui/pickers";
import SingleDropDown from "./SingleDropDown";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";

//Contexts
import { SetupContext } from "../Contexts/SetupContext";

//Styling
import { useStyles } from "../Styles/styles";
import { withStyles } from "@material-ui/core/styles";

//utils
import useWindowSize from "../utils/window-size";
import {
  colorHash,
  handleUuid,
  filterEmptyAlerts,
  formatOfficerName,
} from "../utils/utils";
import { shouldBeLocked } from "../utils/Roles";
import { UserContext } from "../Contexts/UserContext";

const StyledAccordion = withStyles({
  root: {
    border: "1px solid rgba(0, 0, 0, .125)",
    boxShadow: "none",
    "&:not(:last-child)": {
      borderBottom: 0,
    },
    "&:before": {
      display: "none",
    },
    "&$expanded": {
      margin: "auto",
    },
  },
  expanded: {},
})(Accordion);

const StyledAccordionSummary = withStyles({
  root: {
    backgroundColor: "rgba(0, 0, 0, .03)",
    borderBottom: "1px solid rgba(0, 0, 0, .125)",
    marginBottom: -1,
    minHeight: 56,
    "&$expanded": {
      minHeight: 56,
    },
  },
  content: {
    "&$expanded": {
      margin: "12px 0",
    },
  },
  expanded: {},
})(AccordionSummary);

const StyledAccordionDetails = withStyles({
  root: {
    padding: 0,
  },
})(AccordionDetails);

const StyledTableRow = withStyles(theme => ({
  root: {
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover,
    },
  },
}))(TableRow);

import moment from "moment";

const AlertComponent = ({
  parentObj,
  accessor,
  type,
  updateHandler,
  deleteHandler,
  disabled,
  userRoles,
  incidentId,
  pauseLocationPolling,
  resumeLocationPolling,
  ...props
}) => {
  const memoizedLocked = useMemo(
    () => shouldBeLocked(disabled, props?.canEdit, userRoles),
    [disabled, props?.canEdit, userRoles],
  );
  const classes = useStyles();
  const [showModal, setShowModal] = useState(false);
  const [showExpansionPanel, setShowExpansionPanel] = useState(false);
  const windowSize = useWindowSize();

  const [newAlert, setNewAlert] = useState(null);

  useEffect(() => {
    const alertId = handleUuid();
    setNewAlert({
      id: alertId,
      uuid: alertId,
      incidentId: incidentId,
    });
  }, [incidentId]);

  const { setup } = useContext(SetupContext);
  const {
    setupLists: { addressAlertTypes, personAlertTypes, vehicleAlertTypes },
  } = setup;
  const { nightMode, user } = useContext(UserContext);

  const [alertOptions, setAlertOptions] = useState([]);
  useEffect(() => {
    if (type === "address") {
      setAlertOptions(addressAlertTypes);
    }
    if (type === "person") {
      setAlertOptions(personAlertTypes);
    }
    if (type === "vehicle") {
      setAlertOptions(vehicleAlertTypes);
    }
  }, [addressAlertTypes, personAlertTypes, type, vehicleAlertTypes]);

  const inputLabel = useRef(null);
  const [labelWidth, setLabelWidth] = useState(0);

  useEffect(() => {
    if (inputLabel.current) {
      setLabelWidth(inputLabel.current.offsetWidth);
    }
  }, []);

  const confirmDelete = alert => {
    if (window.confirm(`Are you sure you want to deleted alert ${alert.id}?`)) {
      deleteHandler(alert.id);
    } else {
      return;
    }
  };

  const handleChange = event => {
    const value = event.target.value;
    const name = event.target.name;
    setNewAlert(prevState => {
      return { ...prevState, [name]: value };
    });
  };

  const handleDateChange = (value, name, date) => {
    let serverTimeFormat = "";
    if (!date) {
      setNewAlert(prevState => {
        return { ...prevState, [name]: serverTimeFormat };
      });
    } else if (date.isValid()) {
      serverTimeFormat = date.utc().format("YYYY-MM-DD HH:mm:ss UTC");
      setNewAlert(prevState => {
        return { ...prevState, [name]: serverTimeFormat };
      });
    }
  };

  const addAlert = () => {
    const createdAt = moment.utc().format("YYYY-MM-DD HH:mm:ss UTC");
    const officer = user?.officer;
    const updatedAlert = {
      ...newAlert,
      createdAt,
      officer,
    };
    updateHandler(updatedAlert);
    const alertId = handleUuid();
    setNewAlert({
      id: alertId,
      uuid: alertId,
      incidentId: incidentId ? incidentId : null,
      createdAt,
      officer,
    });
    setShowExpansionPanel(false);
  };

  const clearExpansionPanel = () => {
    const alertId = handleUuid();
    setNewAlert({
      id: alertId,
      uuid: alertId,
      incidentId: incidentId ? incidentId : null,
      createdAt: "",
      officer: null,
    });
    setShowExpansionPanel(false);
  };

  const visibleAlerts = useMemo(() => {
    if (!Array.isArray(parentObj?.[accessor])) {
      return [];
    }
    return parentObj[accessor].filter(a => a?.alertType?.id);
  }, [parentObj, accessor]);

  return (
    <div
      style={{ display: "flex", flexDirection: "row", alignItems: "center" }}
    >
      <Tooltip title={"Alerts"}>
        <Badge
          badgeContent={filterEmptyAlerts(visibleAlerts)?.length || 0}
          color="primary"
          overlap="circular"
        >
          <Fab
            disabled={!parentObj?.id}
            data-testid="alert-trigger"
            data-cy="alert-button"
            style={{ backgroundColor: "rgb(240, 200, 79)" }}
            className={classes.fabButton}
            onClick={() => {
              setShowModal(!showModal);
            }}
          >
            <span
              style={{ color: "black", fontWeight: "bold", fontSize: "1.5rem" }}
            >
              !
            </span>
          </Fab>
        </Badge>
      </Tooltip>
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        className={classes.modal}
        data-testid="alert-modal"
        data-cy={"alert-component-" + parentObj?.id}
      >
        <div style={{ backgroundColor: nightMode ? "#616161" : "#fae8ad" }}>
          <h3 style={{ paddingLeft: "16px" }}>Alerts for: {parentObj?.name}</h3>
          <ResizableBox
            width={windowSize.width * 0.78}
            height={windowSize.height * 0.3}
            handle={<DragHandleIcon className={`react-resizable-handle`} />}
            handleSize={[20, 20]}
            axis={"y"}
          >
            <TableContainer
              style={{ width: "100%" }}
              className={classes.tableContainer}
              component={Paper}
            >
              <Table
                stickyHeader
                className={classes.table}
                aria-label="simple table"
              >
                <TableHead>
                  <StyledTableRow>
                    <TableCell align="left">Type</TableCell>
                    <TableCell align="left">Notes</TableCell>
                    <TableCell align="left">Expires At</TableCell>
                    <TableCell align="left">Created By</TableCell>
                    <TableCell align="left">Created At</TableCell>
                    <TableCell align="left">Delete</TableCell>
                  </StyledTableRow>
                </TableHead>
                <TableBody data-cy="availOfficer-table-body">
                  {visibleAlerts &&
                    visibleAlerts.length > 0 &&
                    visibleAlerts.map((alert) => (
                      <StyledTableRow
                        data-cy="availOfficer-table-row"
                        key={alert.id}
                      >
                        <TableCell
                          style={{
                            color:
                              colorHash[alert?.alertType?.alertLevel] || "",
                          }}
                          scope="row"
                        >
                          {alert?.alertType?.name}
                        </TableCell>
                        <TableCell scope="row">{alert?.notes}</TableCell>
                        <TableCell scope="row">
                          {alert?.expireAt
                            ? moment
                                .utc(alert?.expireAt, "YYYY-MM-DD HH:mm:ss UTC")
                                .local()
                                .format("MM/DD/YY")
                            : ""}
                        </TableCell>
                        <TableCell scope="row">
                          {formatOfficerName(alert?.officer) || ""}
                        </TableCell>
                        <TableCell scope="row">
                          {alert?.createdAt
                            ? moment
                                .utc(
                                  alert?.createdAt,
                                  "YYYY-MM-DD HH:mm:ss UTC",
                                )
                                .local()
                                .format("MM/DD/YY")
                            : ""}
                        </TableCell>
                        <TableCell scope="row">
                          <Button
                            onClick={
                              !memoizedLocked
                                ? () => confirmDelete(alert)
                                : null
                            }
                          >
                            Remove
                          </Button>
                        </TableCell>
                      </StyledTableRow>
                    ))}
                </TableBody>
              </Table>

              {visibleAlerts?.length === 0 && (
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                  }}
                >
                  <h3>No Alerts</h3>
                </div>
              )}
            </TableContainer>
          </ResizableBox>
          <div style={{ width: "100%" }}>
            {/* <Button variant="contained">Add Alert</Button> */}
            <StyledAccordion
              nightMode={nightMode}
              square
              expanded={showExpansionPanel}
              onChange={() => {
                setShowExpansionPanel(!showExpansionPanel);
              }}
            >
              <StyledAccordionSummary
                aria-controls="panel1d-content"
                id="panel1d-header"
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <Typography data-cy="initial-call-information-panel-header">
                    Add Alert
                  </Typography>
                  {showExpansionPanel ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </div>
              </StyledAccordionSummary>
              <StyledAccordionDetails onClick={e => e.stopPropagation()}>
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      flexWrap: "flex",
                      width: "100%",
                      alignItems: "center",
                    }}
                  >
                    <SingleDropDown
                      handleChange={handleChange}
                      stateName={"alertType"}
                      value={newAlert?.alertType}
                      options={alertOptions}
                      title={"Alert\xa0Type"}
                      classStyle={classes.dropdown}
                      inputLabel={inputLabel}
                      labelWidth={labelWidth}
                      userRoles={userRoles}
                      {...props}
                    />
                    <TextareaAutosize
                      onChange={handleChange}
                      data-cy="new-alert-notes"
                      style={{
                        backgroundColor: nightMode ? "black" : "white",
                        color: nightMode ? "white" : "black",
                        fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                        fontSize: "1rem",
                        width: "100%",
                      }}
                      margin="dense"
                      id="new-alert-notes-id"
                      value={newAlert?.notes || ""}
                      minRows={2}
                      name="notes"
                      placeholder="Notes"
                      //style={{ width: '50%' }}
                      disabled={memoizedLocked}
                      onFocus={() => {
                        !!pauseLocationPolling && pauseLocationPolling();
                      }}
                      onBlur={() => {
                        !!resumeLocationPolling && resumeLocationPolling();
                      }}
                    />
                    <FormControl
                      variant="standard"
                      className={classes.formControl}
                    >
                      <KeyboardDatePicker
                        data-cy="new-alert-expire-at"
                        disabled={memoizedLocked}
                        clearable
                        showTodayButton
                        disablePast
                        minDateMessage={"Date cannot be in the past"}
                        label="Expire At"
                        // inputProps={{width:'100%'}}
                        value={
                          newAlert?.expireAt
                            ? moment
                                .utc(
                                  newAlert?.expireAt,
                                  "YYYY-MM-DD HH:mm:ss UTC",
                                )
                                .local()
                            : null
                        }
                        onChange={(date, value) =>
                          handleDateChange(value, "expireAt", date)
                        }
                        format="MM/DD/YYYY"
                        style={{ width: "100%" }}
                      />
                    </FormControl>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      flexWrap: "flex",
                      width: "100%",
                      alignItems: "center",
                      justifyContent: "space-around",
                      margin: "1vw 0",
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={!memoizedLocked ? () => addAlert() : null}
                    >
                      Submit
                    </Button>
                    <Button variant="contained" onClick={clearExpansionPanel}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </StyledAccordionDetails>
            </StyledAccordion>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AlertComponent;
